import React, { createContext, useCallback, useContext, useEffect, useRef, useState } from "react";
import { useSnackbar } from "notistack";
import { pollJob } from "@/services/jobs-api";
import type { JobType, TrackedJob } from "@/types/jobs";

// ═══════════════════════════════════════════════════════════════════════════
// Context shape
// ═══════════════════════════════════════════════════════════════════════════

interface BackgroundJobsContextValue {
  jobs: TrackedJob[];
  activeCount: number;
  addJob: (jobId: number, jobType: JobType, entityId: number, service: "core" | "finance") => void;
  clearCompleted: () => void;
}

const BackgroundJobsContext = createContext<BackgroundJobsContextValue | null>(null);

// ═══════════════════════════════════════════════════════════════════════════
// sessionStorage helpers
// ═══════════════════════════════════════════════════════════════════════════

const STORAGE_KEY = "nemesis_bg_jobs";

function loadFromStorage(): TrackedJob[] {
  try {
    const raw = sessionStorage.getItem(STORAGE_KEY);
    if (raw) return JSON.parse(raw) as TrackedJob[];
  } catch {
    // ignorar
  }
  return [];
}

function saveToStorage(jobs: TrackedJob[]): void {
  try {
    sessionStorage.setItem(STORAGE_KEY, JSON.stringify(jobs));
  } catch {
    // ignorar
  }
}

// ═══════════════════════════════════════════════════════════════════════════
// Provider
// ═══════════════════════════════════════════════════════════════════════════

const JOB_TYPE_LABEL: Record<string, string> = {
  BookingReconciliation: "Conciliación de bookings",
  ContractAssignment: "Asignación de contratos",
  EligibleInvoicesReport: "Reporte facturas elegibles",
  CurrencyRateSync: "Sincronización de tasas de cambio",
};

export function BackgroundJobsProvider({ children }: { children: React.ReactNode }) {
  const [jobs, setJobs] = useState<TrackedJob[]>(() => loadFromStorage());
  const { enqueueSnackbar } = useSnackbar();
  // intervalIds: map de jobId → interval handle
  const intervalsRef = useRef<Map<number, ReturnType<typeof setInterval>>>(new Map());

  // Persiste en sessionStorage en cada cambio
  useEffect(() => {
    saveToStorage(jobs);
  }, [jobs]);

  // Inicia polling para un job concreto
  const startPolling = useCallback((jobId: number, service: "core" | "finance") => {
    // Evitar duplicar interval
    if (intervalsRef.current.has(jobId)) return;

    const intervalId = setInterval(async () => {
      try {
        const dto = await pollJob(jobId, service);

        setJobs((prev) => {
          const existing = prev.find((j) => j.id === jobId);
          const wasActive = existing?.status === "Queued" || existing?.status === "Running";
          const nowDone = dto.status === "Completed" || dto.status === "Failed";

          // Notificar solo en la transición activo → terminado
          if (wasActive && nowDone) {
            const label = JOB_TYPE_LABEL[dto.jobType] ?? dto.jobType;
            if (dto.status === "Completed") {
              enqueueSnackbar(`${label} completado`, { variant: "success" });
            } else {
              enqueueSnackbar(`${label} falló`, { variant: "error" });
            }
          }

          const updated = prev.map((j) => {
            if (j.id !== jobId) return j;

            const parsedResult =
              dto.status === "Completed" && dto.resultPayload
                ? (() => {
                    try {
                      return JSON.parse(dto.resultPayload);
                    } catch {
                      return undefined;
                    }
                  })()
                : j.parsedResult;

            return { ...j, ...dto, parsedResult };
          });
          return updated;
        });

        // Detener polling si terminó
        if (dto.status === "Completed" || dto.status === "Failed") {
          clearInterval(intervalsRef.current.get(jobId));
          intervalsRef.current.delete(jobId);
        }
      } catch {
        // Silencioso — se reintentará en el siguiente tick
      }
    }, 3000);

    intervalsRef.current.set(jobId, intervalId);
  }, [enqueueSnackbar]);

  // Al montar: reanudar polling de jobs activos cargados de sessionStorage
  useEffect(() => {
    jobs.forEach((job) => {
      if (job.status === "Queued" || job.status === "Running") {
        startPolling(job.id, job.service);
      }
    });
    // Solo al montar
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Limpiar intervalos al desmontar
  useEffect(() => {
    return () => {
      intervalsRef.current.forEach((id) => clearInterval(id));
      intervalsRef.current.clear();
    };
  }, []);

  const addJob = useCallback(
    (jobId: number, jobType: JobType, entityId: number, service: "core" | "finance") => {
      setJobs((prev) => {
        // No duplicar si ya existe
        if (prev.some((j) => j.id === jobId)) return prev;

        const newJob: TrackedJob = {
          id: jobId,
          jobType,
          status: "Queued",
          entityId,
          service,
          progressPercent: null,
          progressMessage: null,
          startedAt: null,
          completedAt: null,
          resultPayload: null,
          errorMessage: null,
        };
        return [...prev, newJob];
      });

      // Iniciar polling (idempotente si ya existe)
      startPolling(jobId, service);
    },
    [startPolling]
  );

  const clearCompleted = useCallback(() => {
    setJobs((prev) => prev.filter((j) => j.status !== "Completed" && j.status !== "Failed"));
  }, []);

  const activeCount = jobs.filter(
    (j) => j.status === "Queued" || j.status === "Running"
  ).length;

  return (
    <BackgroundJobsContext.Provider value={{ jobs, activeCount, addJob, clearCompleted }}>
      {children}
    </BackgroundJobsContext.Provider>
  );
}

// ═══════════════════════════════════════════════════════════════════════════
// Hook
// ═══════════════════════════════════════════════════════════════════════════

export function useBackgroundJobs(): BackgroundJobsContextValue {
  const ctx = useContext(BackgroundJobsContext);
  if (!ctx) {
    throw new Error("useBackgroundJobs must be used inside BackgroundJobsProvider");
  }
  return ctx;
}
